import { useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import {
  centsToDollarInput,
  formatUsdFromCents,
  groupPlatformCosts,
  localTodayIso,
  shiftIsoDate,
  summarizePlatformCosts,
  type PlatformCostDraft,
  type PlatformCostLedgerRow,
} from "@/lib/platform/platformCostLedger";
import { PlatformCostLedgerList } from "./PlatformCostLedgerList";
import { PlatformSubscriptionCostForm } from "./PlatformSubscriptionCostForm";
import { usePlatformCostLedger } from "./usePlatformCostLedger";

type CostFormState =
  | { kind: "add" }
  | { kind: "edit"; row: PlatformCostLedgerRow }
  | { kind: "replace"; row: PlatformCostLedgerRow };

function blankDraft(today: string): PlatformCostDraft {
  return {
    vendor: "",
    product_name: "",
    category: "other",
    amount_cents: 0,
    cadence: "monthly",
    started_on: today,
    ended_on: null,
    notes: null,
  };
}

function draftFromRow(row: PlatformCostLedgerRow, startedOn: string): PlatformCostDraft {
  return {
    vendor: row.vendor,
    product_name: row.product_name,
    category: row.category,
    amount_cents: row.amount_cents,
    cadence: row.cadence,
    started_on: startedOn,
    ended_on: row.ended_on,
    notes: row.notes,
  };
}

export function PlatformSubscriptionCostsPanel() {
  const ledger = usePlatformCostLedger();
  const today = localTodayIso();
  const [form, setForm] = useState<CostFormState | null>(null);
  const summary = summarizePlatformCosts(ledger.rows, today);
  const groups = groupPlatformCosts(ledger.rows, today);
  const hasRows = ledger.rows.length > 0;

  async function onSubmit(draft: PlatformCostDraft) {
    if (!form) return;
    const ok =
      form.kind === "add"
        ? await ledger.create(draft)
        : form.kind === "edit"
          ? await ledger.update(form.row.id, draft)
          : await ledger.replace(form.row.id, draft);
    if (ok) setForm(null);
  }

  function endRow(row: PlatformCostLedgerRow) {
    const label = `${row.vendor} — ${row.product_name}`;
    if (!window.confirm(`End ${label} on ${today}? The run rate drops it off after today.`)) return;
    void ledger.end(row.id, today);
  }

  function deleteRow(row: PlatformCostLedgerRow) {
    const label = `${row.vendor} — ${row.product_name}`;
    if (!window.confirm(`Delete ${label}? This removes the row from the ledger.`)) return;
    void ledger.remove(row.id);
  }

  const formInitial =
    form?.kind === "edit"
      ? draftFromRow(form.row, form.row.started_on)
      : form?.kind === "replace"
        ? draftFromRow(
            form.row,
            today > form.row.started_on ? today : shiftIsoDate(form.row.started_on, 1),
          )
        : blankDraft(today);

  return (
    <SectionCard
      title="Platform subscription costs"
      description="What it costs to run the app. Active monthly and annual plans roll into one run rate. One-time charges stay on the ledger and stay out of that rate. Change rate keeps the old price so a later profitability check can see what was in effect."
      rightSlot={
        form || ledger.schemaMissing ? null : (
          <button
            type="button"
            onClick={() => setForm({ kind: "add" })}
            className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            Add cost
          </button>
        )
      }
    >
      {ledger.schemaMissing ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800/70 dark:bg-amber-950/30 dark:text-amber-100">
          <div className="font-semibold">Ledger table is not in the database yet</div>
          <p className="mt-1">
            Run <span className="font-mono">docs/sql/platform_cost_ledger_v1.sql</span> in the Supabase SQL editor.
            That script also records Resend Transactional Pro at $20 per month, starting 2026-10-28.
          </p>
        </div>
      ) : null}

      {!ledger.schemaMissing && ledger.error ? (
        <p className="text-sm text-red-700 dark:text-red-300">{ledger.error}</p>
      ) : null}

      {ledger.loading ? <p className="text-sm text-gray-600 dark:text-zinc-300">Loading costs…</p> : null}

      {!ledger.loading && !ledger.schemaMissing ? (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-xl bg-gray-50 p-4 dark:bg-zinc-900/60">
              <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400">Monthly run rate</div>
              <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-zinc-100">
                {formatUsdFromCents(summary.monthlyCents)}
              </div>
            </div>
            <div className="rounded-xl bg-gray-50 p-4 dark:bg-zinc-900/60">
              <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400">Annualized</div>
              <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-zinc-100">
                {formatUsdFromCents(summary.annualizedCents)}
              </div>
            </div>
            <div className="rounded-xl bg-gray-50 p-4 dark:bg-zinc-900/60">
              <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400">Active plans</div>
              <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-zinc-100">{summary.activeCount}</div>
            </div>
          </div>

          {form ? (
            <PlatformSubscriptionCostForm
              key={form.kind === "add" ? "add" : `${form.kind}-${form.row.id}`}
              title={form.kind === "add" ? "Add a cost" : form.kind === "edit" ? "Edit cost" : "Change rate"}
              submitLabel={form.kind === "replace" ? "Save new rate" : "Save"}
              hint={
                form.kind === "replace"
                  ? "The current rate ends the day before this one starts."
                  : form.kind === "edit"
                    ? "Edit corrects this row in place. Use Change rate when the price itself changes."
                    : "Recurring plans count toward the monthly run rate. One-time charges stay on the ledger only."
              }
              initial={formInitial}
              initialAmount={form.kind === "add" ? "" : centsToDollarInput(formInitial.amount_cents)}
              showEndedOn={form.kind === "edit"}
              busy={ledger.busy}
              onCancel={() => setForm(null)}
              onSubmit={(draft) => void onSubmit(draft)}
            />
          ) : null}

          {!hasRows ? (
            <p className="text-sm text-gray-600 dark:text-zinc-300">
              No costs recorded yet. Resend Transactional Pro ($20 / month) is seeded when the SQL script runs.
            </p>
          ) : (
            <div className="space-y-4">
              <PlatformCostLedgerList
                title="Active"
                asOf={today}
                rows={groups.active}
                busy={ledger.busy}
                onEdit={(row) => setForm({ kind: "edit", row })}
                onReplace={(row) => setForm({ kind: "replace", row })}
                onEnd={endRow}
                onDelete={deleteRow}
              />
              <PlatformCostLedgerList
                title="Scheduled"
                asOf={today}
                rows={groups.scheduled}
                busy={ledger.busy}
                onEdit={(row) => setForm({ kind: "edit", row })}
                onReplace={(row) => setForm({ kind: "replace", row })}
                onEnd={endRow}
                onDelete={deleteRow}
              />
              <PlatformCostLedgerList
                title="One-time"
                asOf={today}
                rows={groups.oneTime}
                busy={ledger.busy}
                onEdit={(row) => setForm({ kind: "edit", row })}
                onReplace={(row) => setForm({ kind: "replace", row })}
                onEnd={endRow}
                onDelete={deleteRow}
              />
              <PlatformCostLedgerList
                title="Ended"
                asOf={today}
                rows={groups.ended}
                busy={ledger.busy}
                onEdit={(row) => setForm({ kind: "edit", row })}
                onReplace={(row) => setForm({ kind: "replace", row })}
                onEnd={endRow}
                onDelete={deleteRow}
              />
            </div>
          )}
        </>
      ) : null}
    </SectionCard>
  );
}
